/**
 * Admin · SOC 2 Evidence — packet generator + access-review tooling.
 *
 * Purpose: one screen for the founder (or A-LIGN auditor) to:
 *   - See current control status (32 controls, 5 families)
 *   - Preview the upcoming access-review snapshot
 *   - Submit + sign a quarterly access review
 *   - Download the full evidence ZIP (manifest + controls + access-review + audit-log slice)
 *
 * All endpoints under /api/admin/soc2/* (admin-role gated).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Download, Loader2, ShieldCheck, FileText, Users, ClipboardCheck, RefreshCw, CheckCircle, AlertCircle, Key, Copy, XCircle, Calendar, Building2, ExternalLink, Edit3, Save } from 'lucide-react';
import BrandTitle from '../../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const SOC2Evidence = () => {
  // Belt-and-suspenders auth guard — the route is already nested under
  // <Layout> which redirects to /login when !user, but iter27 audit caught
  // a brief unauth render window. This explicit check ensures the admin
  // controls NEVER paint for an anonymous visitor.
  const { user, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [controls, setControls] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewPreview, setReviewPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewForm, setReviewForm] = useState({ quarter: `${new Date().getFullYear()}-Q${Math.floor(new Date().getMonth() / 3) + 1}`, signature_email: '', notes: '' });
  const [error, setError] = useState('');

  // Auditor token state
  const [auditorTokens, setAuditorTokens] = useState([]);
  const [auditorForm, setAuditorForm] = useState({ auditor_name: '', auditor_email: '', auditor_org: 'A-LIGN', expires_in_days: 30, note: '' });
  const [newlyMinted, setNewlyMinted] = useState(null);  // {token, expires_at, auditor}
  const [mintingAuditor, setMintingAuditor] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Date-range filter for evidence packet (backend supports ?from=&to=)
  const [packetRange, setPacketRange] = useState({ from: '', to: '' });

  // Inline control editing — only the 4 in_progress controls really need evidence_url notes
  const [editingControl, setEditingControl] = useState(null);  // {control_id, evidence_url, notes, status}
  const [savingControl, setSavingControl] = useState(false);

  // Vendor risk (CC3.4)
  const [vendors, setVendors] = useState([]);
  const [vendorSummary, setVendorSummary] = useState(null);
  const [vendorForm, setVendorForm] = useState({ name: '', category: '', criticality: 'high', soc2_type: 'Type II', soc2_report_url: '', dpa_url: '', pentest_url: '', notes: '' });
  const [editingVendor, setEditingVendor] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, c, r, t, v, vs] = await Promise.all([
        axios.get(`${API_URL}/admin/soc2/summary`, { headers: authHeader() }),
        axios.get(`${API_URL}/admin/soc2/control-status`, { headers: authHeader() }),
        axios.get(`${API_URL}/admin/soc2/access-review`, { headers: authHeader() }),
        axios.get(`${API_URL}/admin/soc2/auditor-tokens`, { headers: authHeader() }),
        axios.get(`${API_URL}/admin/soc2/vendors`, { headers: authHeader() }),
        axios.get(`${API_URL}/admin/soc2/vendors/summary`, { headers: authHeader() }),
      ]);
      setSummary(s.data);
      setControls(c.data.items);
      setReviews(r.data.items);
      setAuditorTokens(t.data.items);
      setVendors(v.data.items);
      setVendorSummary(vs.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load SOC 2 data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadReviewPreview = async () => {
    setReviewPreview('loading');
    try {
      const { data } = await axios.get(`${API_URL}/admin/soc2/access-review/current`, { headers: authHeader() });
      setReviewPreview(data);
    } catch (e) {
      setReviewPreview(null);
      setError(e?.response?.data?.detail || 'Failed to preview access review');
    }
  };

  const submitReview = async () => {
    if (!reviewForm.signature_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reviewForm.signature_email)) {
      setError('Signature email required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/admin/soc2/access-review`, reviewForm, { headers: authHeader() });
      setReviewForm({ ...reviewForm, notes: '', signature_email: '' });
      setReviewPreview(null);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPacket = async () => {
    setGenerating(true);
    setError('');
    try {
      const params = {};
      if (packetRange.from) params.from = packetRange.from;
      if (packetRange.to) params.to = packetRange.to;
      const resp = await axios.get(`${API_URL}/admin/soc2/evidence-packet`, {
        headers: authHeader(), responseType: 'blob', params,
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = (packetRange.from || packetRange.to) ? `-${packetRange.from || 'start'}_to_${packetRange.to || 'now'}` : '';
      a.download = `cartieai-soc2-evidence-${new Date().toISOString().slice(0, 10)}${suffix}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Packet download failed');
    } finally {
      setGenerating(false);
    }
  };

  // ----- Inline control editing -----
  const startEditControl = (c) => setEditingControl({
    control_id: c.control_id, status: c.status, evidence_url: c.evidence_url || '', notes: c.notes || '',
  });
  const saveControl = async () => {
    if (!editingControl) return;
    setSavingControl(true);
    setError('');
    try {
      await axios.post(`${API_URL}/admin/soc2/control-status`, editingControl, { headers: authHeader() });
      setEditingControl(null);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Control update failed');
    } finally {
      setSavingControl(false);
    }
  };

  // ----- Auditor token actions ----------------------------------------------
  const mintAuditor = async () => {
    if (!auditorForm.auditor_name || !auditorForm.auditor_email) {
      setError('Auditor name + email required');
      return;
    }
    setMintingAuditor(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/admin/soc2/auditor-tokens`, auditorForm, { headers: authHeader() });
      setNewlyMinted(data);
      setAuditorForm({ auditor_name: '', auditor_email: '', auditor_org: 'A-LIGN', expires_in_days: 30, note: '' });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to mint auditor token');
    } finally {
      setMintingAuditor(false);
    }
  };

  const copyToken = async () => {
    if (!newlyMinted?.token) return;
    try {
      await navigator.clipboard.writeText(newlyMinted.token);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (e) {
      setError('Clipboard copy failed — select manually');
    }
  };

  const revokeAuditor = async (id) => {
    if (!window.confirm('Revoke this auditor token? This is immediate and irreversible.')) return;
    try {
      await axios.post(`${API_URL}/admin/soc2/auditor-tokens/revoke`, { id }, { headers: authHeader() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  // ----- Vendor risk (CC3.4) actions -----
  const saveVendor = async () => {
    if (!vendorForm.name || !vendorForm.category) {
      setError('Vendor name + category required');
      return;
    }
    try {
      const payload = { ...vendorForm };
      // Strip empty URL fields (Pydantic HttpUrl rejects "")
      ['soc2_report_url', 'dpa_url', 'pentest_url', 'status_url'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      if (editingVendor) payload.id = editingVendor;
      await axios.post(`${API_URL}/admin/soc2/vendors`, payload, { headers: authHeader() });
      setVendorForm({ name: '', category: '', criticality: 'high', soc2_type: 'Type II', soc2_report_url: '', dpa_url: '', pentest_url: '', notes: '' });
      setEditingVendor(null);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Vendor save failed');
    }
  };
  const startEditVendor = (v) => {
    setEditingVendor(v.id);
    setVendorForm({
      name: v.name, category: v.category, criticality: v.criticality,
      soc2_type: v.soc2_type || '', soc2_report_url: v.soc2_report_url || '',
      dpa_url: v.dpa_url || '', pentest_url: v.pentest_url || '', notes: v.notes || '',
    });
  };
  const markVendorReviewed = async (id) => {
    try {
      await axios.post(`${API_URL}/admin/soc2/vendors/${id}/review`, {}, { headers: authHeader() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Mark reviewed failed');
    }
  };
  const removeVendor = async (id) => {
    if (!window.confirm('Remove this vendor row?')) return;
    try {
      await axios.delete(`${API_URL}/admin/soc2/vendors/${id}`, { headers: authHeader() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login?next=/admin/soc2/evidence" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 py-10 px-4 sm:px-6 lg:px-8" data-testid="admin-soc2-evidence">
      <Helmet><title>SOC 2 Evidence · Admin · CARTIEAI</title></Helmet>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-2">
              <ShieldCheck className="w-3 h-3" /> Admin · SOC 2 Evidence
            </div>
            <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>SOC 2 Type I Evidence Collection</BrandTitle></h1>
            <p className="text-sm text-slate-600 mt-1">
              Auditor: <span className="font-semibold text-slate-700 dark:text-slate-700">A-LIGN</span> · Target report date: <span className="font-semibold text-emerald-400 dark:text-emerald-400">Jun 15, 2026</span> · Type II: Dec 2026
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex gap-2 items-end">
              <div>
                <label className="text-[10px] text-slate-600 uppercase tracking-widest font-mono mb-1 block">From</label>
                <Input type="date" value={packetRange.from} onChange={(e) => setPacketRange({ ...packetRange, from: e.target.value })} className="h-9 w-36" data-testid="soc2-packet-from" />
              </div>
              <div>
                <label className="text-[10px] text-slate-600 uppercase tracking-widest font-mono mb-1 block">To</label>
                <Input type="date" value={packetRange.to} onChange={(e) => setPacketRange({ ...packetRange, to: e.target.value })} className="h-9 w-36" data-testid="soc2-packet-to" />
              </div>
            </div>
            <Button variant="outline" onClick={refresh} className="gap-2" data-testid="soc2-refresh-btn">
              <RefreshCw className="w-4 h-4" />Refresh
            </Button>
            <Button onClick={downloadPacket} disabled={generating} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2" data-testid="soc2-download-packet-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {generating ? 'Generating…' : 'Download evidence ZIP'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />{error}
          </div>
        )}

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <Card><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Total controls</div>
              <div className="text-3xl font-bold text-slate-900">{summary.controls.total}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-emerald-500">Implemented</div>
              <div className="text-3xl font-bold text-emerald-400 dark:text-emerald-400">{summary.controls.implemented}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-500">In progress</div>
              <div className="text-3xl font-bold text-amber-400 dark:text-amber-400">{summary.controls.in_progress}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Latest review</div>
              <div className="text-base font-bold text-slate-900">{summary.latest_access_review?.quarter || '—'}</div>
              <div className="text-[10px] text-slate-600 mt-0.5 truncate">{fmtDate(summary.latest_access_review?.submitted_at)}</div>
            </CardContent></Card>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-8">

          {/* Quarterly Access Review */}
          <Card data-testid="soc2-access-review-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><ClipboardCheck className="w-5 h-5 text-emerald-500" />Quarterly Access Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                Snapshot every user's role + last-login, then sign immutably. A-LIGN needs one signed review per quarter for CC6.3.
              </p>
              <Button variant="outline" onClick={loadReviewPreview} className="w-full gap-2" data-testid="soc2-preview-btn">
                <Users className="w-4 h-4" />Preview snapshot
              </Button>
              {reviewPreview === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-emerald-500 mx-auto" />}
              {reviewPreview && reviewPreview !== 'loading' && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm" data-testid="soc2-preview-result">
                  <div className="font-semibold text-emerald-300 dark:text-emerald-300">{reviewPreview.count} users in snapshot · {fmtDate(reviewPreview.snapshot_at)}</div>
                  <div className="text-xs text-slate-600 mt-1">Submit below to sign + freeze this snapshot.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Quarter (e.g. 2026-Q1)" value={reviewForm.quarter} onChange={(e) => setReviewForm({ ...reviewForm, quarter: e.target.value })} data-testid="soc2-quarter-input" />
                <Input placeholder="Signature email" type="email" value={reviewForm.signature_email} onChange={(e) => setReviewForm({ ...reviewForm, signature_email: e.target.value })} data-testid="soc2-signature-input" />
              </div>
              <Input placeholder="Notes (optional)" value={reviewForm.notes} onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })} data-testid="soc2-notes-input" />
              <Button onClick={submitReview} disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2" data-testid="soc2-submit-review-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Submit + sign quarterly review
              </Button>
            </CardContent>
          </Card>

          {/* Past reviews */}
          <Card data-testid="soc2-past-reviews-card">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><FileText className="w-5 h-5 text-emerald-500" />Past Access Reviews</CardTitle></CardHeader>
            <CardContent>
              {reviews.length === 0 ? (
                <p className="text-sm text-slate-600 italic">No reviews submitted yet.</p>
              ) : (
                <div className="space-y-2">
                  {reviews.slice(0, 5).map((r) => (
                    <div key={r.id} className="p-3 rounded-lg bg-slate-800 text-slate-100 text-sm flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-white">{r.quarter} · {r.user_count} users</div>
                        <div className="text-[11px] text-slate-400">{fmtDate(r.submitted_at)} · by {r.submitted_by}</div>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">{r.signature_email}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Auditor signed-URL access — A-LIGN auditor handoff (Feb 16 2026) */}
        <Card className="mb-8" data-testid="soc2-auditor-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="w-5 h-5 text-emerald-500" />A-LIGN Auditor Access (Signed URLs)
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Mint a 30-day read-only token for your auditor instead of creating a full CARTIE user. The auditor can then call
              <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-800 text-emerald-400 dark:text-emerald-400 text-[11px]">/api/admin/soc2/auditor/*</code>
              with a bearer token. All accesses are logged. Revoke any time.
            </p>
          </CardHeader>
          <CardContent>
            {/* Newly minted token banner — shown ONCE */}
            {newlyMinted && (
              <div className="mb-5 rounded-lg border-2 border-emerald-400/50 bg-emerald-500/10 p-4" data-testid="soc2-newly-minted">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-emerald-100 mb-1">
                      Token minted for {newlyMinted.auditor} · expires {new Date(newlyMinted.expires_at).toLocaleDateString()}
                    </div>
                    <p className="text-xs text-emerald-200/80 mb-3">
                      Copy now — this is the ONLY time you'll see this token. Share via 1Password / Bitwarden, NOT email.
                    </p>
                    <div className="flex items-center gap-2 p-2 rounded bg-slate-900 border border-emerald-500/30 font-mono text-xs text-emerald-300 break-all">
                      <span className="flex-1 select-all" data-testid="soc2-token-value">{newlyMinted.token}</span>
                      <Button size="sm" onClick={copyToken} variant="outline" className="border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 shrink-0 gap-1" data-testid="soc2-copy-token">
                        <Copy className="w-3 h-3" />{copySuccess ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <button type="button" onClick={() => setNewlyMinted(null)} className="text-emerald-400 hover:text-white text-xl leading-none" title="Dismiss">×</button>
                </div>
              </div>
            )}

            {/* Mint form */}
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <Input placeholder="Auditor name" value={auditorForm.auditor_name} onChange={(e) => setAuditorForm({ ...auditorForm, auditor_name: e.target.value })} data-testid="soc2-auditor-name" />
              <Input type="email" placeholder="Auditor email" value={auditorForm.auditor_email} onChange={(e) => setAuditorForm({ ...auditorForm, auditor_email: e.target.value })} data-testid="soc2-auditor-email" />
            </div>
            <div className="grid sm:grid-cols-3 gap-2 mb-2">
              <Input placeholder="Org (default A-LIGN)" value={auditorForm.auditor_org} onChange={(e) => setAuditorForm({ ...auditorForm, auditor_org: e.target.value })} data-testid="soc2-auditor-org" />
              <Input type="number" min="1" max="90" placeholder="Days" value={auditorForm.expires_in_days} onChange={(e) => setAuditorForm({ ...auditorForm, expires_in_days: parseInt(e.target.value || '30', 10) })} data-testid="soc2-auditor-days" />
              <Input placeholder="Note (optional)" value={auditorForm.note} onChange={(e) => setAuditorForm({ ...auditorForm, note: e.target.value })} data-testid="soc2-auditor-note" />
            </div>
            <Button onClick={mintAuditor} disabled={mintingAuditor} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2" data-testid="soc2-mint-token-btn">
              {mintingAuditor ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Mint auditor token
            </Button>

            {/* Token list */}
            {auditorTokens.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-slate-600 font-mono mb-2">Active + revoked tokens</div>
                <div className="space-y-2">
                  {auditorTokens.map((t) => {
                    const expired = new Date(t.expires_at) < new Date();
                    const revoked = !!t.revoked_at;
                    return (
                      <div key={t.id} className="p-3 rounded-lg bg-slate-800 text-slate-100 flex items-center justify-between gap-3" data-testid={`soc2-token-${t.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-white truncate">
                            {t.auditor_name} <span className="text-slate-400 font-normal">·</span> {t.auditor_email}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {t.auditor_org} · Issued {fmtDate(t.issued_at)} by {t.issued_by} · Used {t.use_count}× · Last: {fmtDate(t.last_used_at)}
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {revoked ? <Badge className="bg-slate-300 text-slate-200 dark:bg-slate-700 dark:text-slate-300 border-0">REVOKED</Badge>
                            : expired ? <Badge className="bg-amber-500/20 text-amber-300 dark:bg-amber-900/30 dark:text-amber-400 border-0">EXPIRED</Badge>
                            : <Badge className="bg-emerald-500/20 text-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">ACTIVE</Badge>}
                          {!revoked && !expired && (
                            <Button size="sm" variant="outline" onClick={() => revokeAuditor(t.id)} className="border-rose-500/40 text-rose-400 dark:text-rose-300 hover:bg-rose-500/10 gap-1" data-testid={`soc2-revoke-${t.id}`}>
                              <XCircle className="w-3 h-3" />Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Controls table */}
        <Card data-testid="soc2-controls-card">
          <CardHeader>
            <CardTitle className="text-lg">32-Control Status</CardTitle>
            <p className="text-sm text-slate-600">Source-of-truth for A-LIGN. Updates flow into the next evidence packet.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-emerald-500/20 text-left text-[11px] uppercase tracking-widest text-slate-600">
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Family</th>
                    <th className="py-2 pr-3">Control</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Evidence URL</th>
                    <th className="py-2 pr-3">Updated</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {controls.map((c) => {
                    const isEditing = editingControl?.control_id === c.control_id;
                    return (
                      <tr key={c.control_id} className="border-b border-slate-100 dark:border-slate-800/50 align-top" data-testid={`soc2-row-${c.control_id}`}>
                        <td className="py-2.5 pr-3 font-mono text-emerald-400 dark:text-emerald-400 text-xs">{c.control_id}</td>
                        <td className="py-2.5 pr-3 text-xs text-slate-600">{c.family}</td>
                        <td className="py-2.5 pr-3 text-slate-900 dark:text-slate-700">{c.label}</td>
                        <td className="py-2.5 pr-3">
                          {isEditing ? (
                            <select
                              value={editingControl.status}
                              onChange={(e) => setEditingControl({ ...editingControl, status: e.target.value })}
                              className="h-7 text-xs rounded border border-emerald-500/30 dark:border-slate-700 bg-slate-900 dark:bg-slate-800 px-2"
                              data-testid={`soc2-status-select-${c.control_id}`}
                            >
                              <option value="implemented">implemented</option>
                              <option value="in_progress">in_progress</option>
                              <option value="not_applicable">not_applicable</option>
                            </select>
                          ) : (
                            <Badge className={c.status === 'implemented'
                              ? 'bg-emerald-500/20 text-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 border-0'
                              : 'bg-amber-500/20 text-amber-300 dark:bg-amber-900/30 dark:text-amber-400 border-0'}>
                              {c.status}
                            </Badge>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs max-w-[240px]">
                          {isEditing ? (
                            <Input
                              value={editingControl.evidence_url}
                              onChange={(e) => setEditingControl({ ...editingControl, evidence_url: e.target.value })}
                              placeholder="https://drive.google.com/…"
                              className="h-7 text-xs"
                              data-testid={`soc2-url-input-${c.control_id}`}
                            />
                          ) : c.evidence_url ? (
                            <a href={c.evidence_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 dark:text-emerald-400 hover:underline inline-flex items-center gap-1 truncate max-w-full">
                              <ExternalLink className="w-3 h-3 shrink-0" /><span className="truncate">{c.evidence_url}</span>
                            </a>
                          ) : (
                            <span className="text-slate-400 italic text-[11px]">—</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-[11px] text-slate-600 whitespace-nowrap">{fmtDate(c.updated_at)}</td>
                        <td className="py-2.5 pr-3">
                          {isEditing ? (
                            <div className="flex gap-1">
                              <Button size="sm" onClick={saveControl} disabled={savingControl} className="h-7 px-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid={`soc2-save-${c.control_id}`}>
                                {savingControl ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingControl(null)} className="h-7 px-2">
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => startEditControl(c)} className="h-7 px-2 text-slate-600 hover:text-emerald-500" data-testid={`soc2-edit-${c.control_id}`}>
                              <Edit3 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Vendor Risk (CC3.4) */}
        <Card className="mt-8" data-testid="soc2-vendor-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-emerald-500" />Vendor Risk · CC3.4 evidence
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Catalogue every third-party vendor with their SOC 2 / DPA / pen-test artifacts. Reviewing each quarterly satisfies CC3.4.
              All rows ship inside the evidence ZIP automatically (<code className="text-[11px] bg-slate-800 px-1 py-0.5 rounded">04_vendor_risk.json + .csv</code>).
            </p>
          </CardHeader>
          <CardContent>
            {/* Vendor summary */}
            {vendorSummary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                <div className="rounded bg-slate-800 text-slate-100 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Total vendors</div>
                  <div className="text-xl font-bold text-white">{vendorSummary.total}</div>
                </div>
                <div className="rounded bg-emerald-500/10 dark:bg-emerald-900/20 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 dark:text-emerald-400">Reviewed</div>
                  <div className="text-xl font-bold text-emerald-300 dark:text-emerald-400">{vendorSummary.reviewed}<span className="text-xs text-emerald-500/70">/{vendorSummary.total}</span></div>
                </div>
                <div className="rounded bg-emerald-500/10 dark:bg-cyan-900/20 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 dark:text-cyan-400">With SOC 2 report</div>
                  <div className="text-xl font-bold text-cyan-300 dark:text-cyan-400">{vendorSummary.with_soc2_report}</div>
                </div>
                <div className={`rounded p-2.5 ${vendorSummary.unreviewed_critical_high > 0 ? 'bg-rose-500/10 dark:bg-rose-900/20' : 'bg-slate-800'}`}>
                  <div className={`text-[10px] uppercase tracking-wider ${vendorSummary.unreviewed_critical_high > 0 ? 'text-rose-300 dark:text-rose-400' : 'text-slate-300'}`}>Unreviewed (crit/high)</div>
                  <div className={`text-xl font-bold ${vendorSummary.unreviewed_critical_high > 0 ? 'text-rose-300 dark:text-rose-400' : 'text-white'}`}>{vendorSummary.unreviewed_critical_high}</div>
                </div>
              </div>
            )}

            {/* Vendor add/edit form */}
            <div className="mb-5 p-4 rounded-lg border border-emerald-500/20 bg-slate-900 text-slate-100 dark:bg-slate-900">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-2">
                {editingVendor ? 'Edit vendor' : 'Add or upsert vendor'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                <Input placeholder="Name" value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} data-testid="vendor-name" />
                <Input placeholder="Category" value={vendorForm.category} onChange={(e) => setVendorForm({ ...vendorForm, category: e.target.value })} data-testid="vendor-category" />
                <select value={vendorForm.criticality} onChange={(e) => setVendorForm({ ...vendorForm, criticality: e.target.value })} className="h-9 text-sm rounded border border-emerald-500/30 dark:border-slate-700 bg-slate-900 dark:bg-slate-800 px-2" data-testid="vendor-criticality">
                  <option value="critical">critical</option><option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
                </select>
                <Input placeholder="SOC 2 Type" value={vendorForm.soc2_type} onChange={(e) => setVendorForm({ ...vendorForm, soc2_type: e.target.value })} data-testid="vendor-soc2-type" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <Input placeholder="SOC 2 report URL" value={vendorForm.soc2_report_url} onChange={(e) => setVendorForm({ ...vendorForm, soc2_report_url: e.target.value })} data-testid="vendor-soc2-url" />
                <Input placeholder="DPA URL" value={vendorForm.dpa_url} onChange={(e) => setVendorForm({ ...vendorForm, dpa_url: e.target.value })} data-testid="vendor-dpa-url" />
                <Input placeholder="Pen-test URL" value={vendorForm.pentest_url} onChange={(e) => setVendorForm({ ...vendorForm, pentest_url: e.target.value })} data-testid="vendor-pentest-url" />
              </div>
              <Input placeholder="Notes" value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} className="mb-3" data-testid="vendor-notes" />
              <div className="flex gap-2">
                <Button onClick={saveVendor} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-1" data-testid="vendor-save-btn">
                  <Save className="w-4 h-4" />{editingVendor ? 'Update vendor' : 'Save vendor'}
                </Button>
                {editingVendor && (
                  <Button variant="outline" onClick={() => { setEditingVendor(null); setVendorForm({ name: '', category: '', criticality: 'high', soc2_type: 'Type II', soc2_report_url: '', dpa_url: '', pentest_url: '', notes: '' }); }}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Vendor table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-emerald-500/20 text-left text-[11px] uppercase tracking-widest text-slate-600">
                    <th className="py-2 pr-3">Vendor</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Criticality</th>
                    <th className="py-2 pr-3">Artifacts</th>
                    <th className="py-2 pr-3">Last reviewed</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr key={v.id} className="border-b border-slate-100 dark:border-slate-800/50" data-testid={`vendor-row-${v.id}`}>
                      <td className="py-2.5 pr-3 text-slate-900 dark:text-slate-100 font-medium">{v.name}</td>
                      <td className="py-2.5 pr-3 text-xs text-slate-600">{v.category}</td>
                      <td className="py-2.5 pr-3">
                        <Badge className={
                          v.criticality === 'critical' ? 'bg-rose-500/20 text-rose-300 dark:bg-rose-900/30 dark:text-rose-400 border-0'
                          : v.criticality === 'high' ? 'bg-amber-500/20 text-amber-300 dark:bg-amber-900/30 dark:text-amber-400 border-0'
                          : 'bg-slate-200 text-slate-200 dark:bg-slate-700 dark:text-slate-300 border-0'
                        }>{v.criticality}</Badge>
                      </td>
                      <td className="py-2.5 pr-3 text-xs space-x-2">
                        {v.soc2_report_url && <a href={v.soc2_report_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />SOC 2</a>}
                        {v.dpa_url && <a href={v.dpa_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />DPA</a>}
                        {v.pentest_url && <a href={v.pentest_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />Pen-test</a>}
                        {!v.soc2_report_url && !v.dpa_url && !v.pentest_url && <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-[11px] text-slate-600 whitespace-nowrap">
                        {v.last_reviewed_at ? <span>{fmtDate(v.last_reviewed_at)}<br /><span className="text-[10px] text-slate-400">by {v.last_reviewed_by}</span></span> : <span className="italic">never</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => markVendorReviewed(v.id)} title="Mark reviewed today" className="h-7 px-2 text-emerald-400 hover:bg-emerald-500/10 dark:hover:bg-emerald-900/20" data-testid={`vendor-review-${v.id}`}>
                            <Calendar className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => startEditVendor(v)} className="h-7 px-2 text-slate-600 hover:text-emerald-500" data-testid={`vendor-edit-${v.id}`}>
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removeVendor(v.id)} className="h-7 px-2 text-rose-500 hover:text-rose-300" data-testid={`vendor-delete-${v.id}`}>
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default SOC2Evidence;
